import { useState } from 'react';

import { DownOutlined, EllipsisOutlined, UpOutlined } from '@ant-design/icons';
import {
  CARD_CODES,
  CardCodes,
  CARD_LOGIN_TYPE_BY_ORGANIZATION_CODE,
  CardLoginType,
  CorporateCardConnection,
} from '@revenuemarket/bank';
import { Colors, typographyStyle, CDS_ZINDEX, Dropdown, Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { ClickArea } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { Drawer, Typography, Button, Alert, Popconfirm, Divider, Modal } from 'antd';

import CardAvailableDateInfoModal from '~components/Card/CardAvailableDateInfoModal';
import useGetCorporateCardConnections, {
  usePurgeCorporateCardConnection,
  useDepurgeCorporateCardConnection,
} from '~hooks/queries/useCorporateCardConnections';
import { useCompanyId } from '~hooks/useCompanyId';

import AlertConnectInfo from './AlertConnectInfo';
import { CardConnectionItem } from './CardConnectionItem';
import { CardEditModalContent } from './CardEditModalContent';
import { CardOrganizationConnectDrawer } from './CardOrganizationConnectDrawer';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationCode: CardCodes;
  initScrape?: boolean;
}

const AlertForEmptyOrganization = ({ organizationCode }: { organizationCode: CardCodes }) => {
  const loginType =
    CARD_LOGIN_TYPE_BY_ORGANIZATION_CODE[
      organizationCode as keyof typeof CARD_LOGIN_TYPE_BY_ORGANIZATION_CODE
    ][0];
  const organizationName = CARD_CODES[organizationCode];
  const message =
    loginType === CardLoginType.LOGIN_ID
      ? `${organizationName} 법인 홈페이지 ID/PW 계정을 추가해주세요.`
      : `${organizationName} 법인 홈페이지에 로그인 가능한 공동인증서를 추가해주세요.`;
  return <Alert showIcon banner style={{ gap: 8 }} type="info" message={message} />;
};

export const CardOrganizationConnectionDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationCode,
  initScrape = false,
}) => {
  const { companyId } = useCompanyId();
  const { data: connectionData } = useGetCorporateCardConnections(companyId as string);

  const connections =
    connectionData?.Data?.data?.connections?.filter(
      (connection: CorporateCardConnection) =>
        connection.organization.organizationCode === organizationCode,
    ) || [];

  const normalConnections = connections.filter(
    (connection: CorporateCardConnection) => !connection.purgedAt,
  );
  const purgedConnections = connections.filter(
    (connection: CorporateCardConnection) => connection.purgedAt,
  );

  const organizationName = CARD_CODES[organizationCode];

  const [showPurgedConnections, setShowPurgedConnections] = useState(false);

  const CardConnectionActions = ({ connection }: { connection: CorporateCardConnection }) => {
    const { mutateAsync: purgeCorporateCardConnection } = usePurgeCorporateCardConnection(
      companyId,
      connection.id,
    );

    const handleDelete = () => {
      Modal.confirm({
        title: '아래 계정을 연동 중단하시겠습니까?',
        okButtonProps: {
          danger: true,
        },
        zIndex: CDS_ZINDEX.DRAWER.BASE + 1000,
        okText: '연동 중단',
        onOk: async () => {
          const response = await purgeCorporateCardConnection();
          if (response.Status === ResponseStatus.SUCCESS) {
            Toast.success('카드 계정이 삭제됐어요.');
          } else {
            Toast.error('연동 중단에 실패했습니다.');
          }
        },
        cancelText: '취소',
        content: (
          <Flex direction="column" gap={8}>
            <Typography.Text>{`• ${
              connection.loginName.length > 0 ? connection.loginName : '삭제된 공동인증서'
            }`}</Typography.Text>
            <Typography.Text>
              연동 중단된 계정은 더이상 수집되지 않습니다. 추후 재연동할 수 있으나, 카드사별 수집
              가능 기간에 따라 수집이 중단된 기간의 데이터는 재연동하더라도 불러오지 못할 수도
              있으니 유의하세요.
            </Typography.Text>
            <Typography.Link
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => (
                  <CardAvailableDateInfoModal
                    open={isOpen}
                    onCancel={onClose}
                    zIndex={CDS_ZINDEX.DRAWER.BASE + 2000}
                  />
                ));
              }}
            >
              카드사별 수집 가능 기간
            </Typography.Link>
          </Flex>
        ),
      });
    };

    const dropdownMenu = {
      items: [
        {
          key: 'edit',
          label: <span style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>수정</span>,
          onClick: () => {
            overlays.open(({ isOpen, onClose }) => (
              <CardEditModalContent
                isOpen={isOpen}
                onClose={onClose}
                connection={connection}
                organizationCode={organizationCode}
              />
            ));
          },
        },
        {
          key: 'delete',
          label: <span style={{ ...typographyStyle['14_400'], color: '#da2333' }}>연동 중단</span>,
          onClick: handleDelete,
        },
      ],
    };

    return (
      <Dropdown menu={dropdownMenu}>
        <Button
          type="text"
          icon={<EllipsisOutlined />}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(29, 34, 37, 0.12)',
            borderRadius: 8,
          }}
        />
      </Dropdown>
    );
  };

  const DepurgeCardConnection = ({ connectionId }: { connectionId: number }) => {
    const { mutateAsync: depurgeCorporateCardConnection } = useDepurgeCorporateCardConnection(
      companyId,
      connectionId,
    );

    return (
      <Popconfirm
        title="정말 재연동하시겠습니까?"
        onConfirm={async () => {
          const response = await depurgeCorporateCardConnection();
          if (response.Status === ResponseStatus.SUCCESS) {
            Toast.success('재연동 되었습니다.');
          } else {
            Toast.error('재연동에 실패했습니다.');
          }
        }}
      >
        <Button type="link">재연동</Button>
      </Popconfirm>
    );
  };

  const openAnotherOrganizationConnectionDrawer = (organizationCode: CardCodes) => {
    onClose();
    overlays.open(({ isOpen, onClose }) => (
      <CardOrganizationConnectionDrawer
        isOpen={isOpen}
        onClose={onClose}
        organizationCode={organizationCode}
      />
    ));
  };

  return (
    <Drawer
      placement="right"
      title={`${CARD_CODES[organizationCode]} 연결`}
      open={isOpen}
      onClose={onClose}
    >
      <Flex direction="column" gap={24}>
        {organizationCode === '305' && (
          <Flex gap={8} direction="column">
            <AlertConnectInfo
              organizationCode={organizationCode}
              onChangeOrganizationCode={openAnotherOrganizationConnectionDrawer}
            />
          </Flex>
        )}
        <Flex direction="column" gap={24}>
          <Typography.Text>{`${organizationName} 연동 계정 ${connections.length}건`}</Typography.Text>
          {connections.length === 0 && (
            <AlertForEmptyOrganization organizationCode={organizationCode} />
          )}
          <Flex direction="column" gap={16} align="center">
            {connections.length === 0 ? (
              <Typography.Text type="secondary">연동 계정이 없습니다.</Typography.Text>
            ) : (
              normalConnections.map((connection: CorporateCardConnection) => (
                <CardConnectionItem
                  key={connection.id}
                  {...connection}
                  organizationCode={organizationCode}
                  organizationName={CARD_CODES[organizationCode]}
                  actionable={<CardConnectionActions connection={connection} />}
                />
              ))
            )}
          </Flex>

          <Button
            type="primary"
            onClick={() => {
              overlays.open(({ isOpen, onClose }) => (
                <CardOrganizationConnectDrawer
                  organizationCode={organizationCode}
                  initScrape={initScrape}
                  isOpen={isOpen}
                  onClose={onClose}
                  onChangeOrganizationCode={(organizationCode) => {
                    onClose();
                    openAnotherOrganizationConnectionDrawer(organizationCode);
                  }}
                />
              ));
            }}
          >
            + 계정 추가
          </Button>
        </Flex>
        {purgedConnections.length > 0 && (
          <>
            <Divider style={{ margin: 0 }} />
            {showPurgedConnections && (
              <>
                <Typography.Text>{`${organizationName} 연동 중단 계정 ${purgedConnections.length}건`}</Typography.Text>
                <Flex direction="column" gap={16} align="center">
                  {purgedConnections.map((connection: CorporateCardConnection) => (
                    <CardConnectionItem
                      key={connection.id}
                      {...connection}
                      organizationCode={organizationCode}
                      organizationName={CARD_CODES[organizationCode]}
                      actionable={<DepurgeCardConnection connectionId={connection.id} />}
                    />
                  ))}
                </Flex>
              </>
            )}
            <ClickArea onClick={() => setShowPurgedConnections(!showPurgedConnections)}>
              <Flex justify="center" align="center" gap={4}>
                <Typography.Text color={theme.colorTokens.textDescription}>
                  {showPurgedConnections ? '연동 중단 계정 숨기기' : '연동 중단 계정 보기'}
                </Typography.Text>
                {showPurgedConnections ? <UpOutlined /> : <DownOutlined />}
              </Flex>
            </ClickArea>
          </>
        )}
      </Flex>
    </Drawer>
  );
};
